import { DataResult, DataSourceRequestState } from '@progress/kendo-data-query';
import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  ApiError,
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  MixtureAvgParameterDto,
  MixtureDto,
  MixtureEditDto,
  MixtureSpecificationDto,
  MixtureStatusDto,
  MixtureStdPracticeDto
} from 'src/store/mixtures/mixture';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum MixtureTypes {
  FetchAll = 'mixtures/FetchAll',
  FetchFiltered = 'mixtures/FetchFiltered',
  FetchStatuses = 'mixtures/FetchStatuses',
  Add = 'mixtures/Add',
  Update = 'mixtures/Update',
  Delete = 'mixtures/Delete',
  FetchMixtureAvgParameters = 'mixtures/FetchMixtureAvgParameters',
  FetchMixtureStdPractice = 'mixtures/FetchMixtureStdPractice',
  FetchMixtureSpecifications = 'mixtures/FetchMixtureSpecifications',
  FetchNumberOfOngoingMixtures = 'mixtures/FetchNumberOfOngoingMixtures',
  Failure = 'mixtures/Failure',
  UpdateRequestState = 'mixtures/UpdateRequestState'
}

export type MixtureAction =
  | { type: MixtureTypes.FetchAll; mixtures: MixtureDto[] }
  | { type: MixtureTypes.FetchStatuses; mixtureStatuses: MixtureStatusDto[] }
  | { type: MixtureTypes.FetchFiltered; filteredMixtures: DataResult }
  | { type: MixtureTypes.Add; mixture: MixtureDto }
  | { type: MixtureTypes.Update; mixture: MixtureDto }
  | { type: MixtureTypes.Delete; mixture: MixtureDto }
  | {
      type: MixtureTypes.FetchMixtureAvgParameters;
      mixtureAvgParameters: MixtureAvgParameterDto[];
    }
  | {
      type: MixtureTypes.FetchMixtureStdPractice;
      mixtureStdPractice: MixtureStdPracticeDto[];
    }
  | {
      type: MixtureTypes.FetchMixtureSpecifications;
      mixtureSpecifications: MixtureSpecificationDto[];
    }
  | { type: MixtureTypes.Failure; errors: ApiError }
  | {
      type: MixtureTypes.UpdateRequestState;
      requestState: DataSourceRequestState;
    }
  | {
      type: MixtureTypes.FetchNumberOfOngoingMixtures;
      count: number;
    };

export const mixtureActions = {
  fetchAll: (mixtures: MixtureDto[]): MixtureAction => ({
    type: MixtureTypes.FetchAll,
    mixtures
  }),
  fetchFiltered: (filteredMixtures: DataResult): MixtureAction => ({
    type: MixtureTypes.FetchFiltered,
    filteredMixtures
  }),
  fetchStatuses: (mixtureStatuses: MixtureStatusDto[]): MixtureAction => ({
    type: MixtureTypes.FetchStatuses,
    mixtureStatuses
  }),
  add: (mixture: MixtureDto): MixtureAction => ({
    type: MixtureTypes.Add,
    mixture
  }),
  update: (mixture: MixtureDto): MixtureAction => ({
    type: MixtureTypes.Update,
    mixture
  }),
  delete: (mixture: MixtureDto): MixtureAction => ({
    type: MixtureTypes.Delete,
    mixture
  }),
  fetchMixtureAvgParameters: (
    mixtureAvgParameters: MixtureAvgParameterDto[]
  ): MixtureAction => ({
    type: MixtureTypes.FetchMixtureAvgParameters,
    mixtureAvgParameters
  }),
  fetchMixtureStdPractice: (
    mixtureStdPractice: MixtureStdPracticeDto[]
  ): MixtureAction => ({
    type: MixtureTypes.FetchMixtureStdPractice,
    mixtureStdPractice
  }),
  fetchMixtureSpecifications: (
    mixtureSpecifications: MixtureSpecificationDto[]
  ): MixtureAction => ({
    type: MixtureTypes.FetchMixtureSpecifications,
    mixtureSpecifications
  }),
  fetchNumberOfOngoingMixtures: (count: number): MixtureAction => ({
    type: MixtureTypes.FetchNumberOfOngoingMixtures,
    count
  }),
  requestFailure: (errors: ApiError): MixtureAction => ({
    type: MixtureTypes.Failure,
    errors
  }),
  updateRequestState: (
    requestState: DataSourceRequestState
  ): MixtureAction => ({
    type: MixtureTypes.UpdateRequestState,
    requestState
  })
};

export const fetchMixtures = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(MixtureTypes.FetchAll));
    const { ok, data, error } = await getRequest<MixtureDto[]>('/mixtures');
    dispatch(requestEnd(MixtureTypes.FetchAll));
    return ok
      ? dispatch(mixtureActions.fetchAll(data))
      : dispatch(mixtureActions.requestFailure(error!));
  };
};

export const fetchMixtureStatuses = () => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await getRequest<MixtureStatusDto[]>(
      '/mixtures/statuses'
    );
    return ok
      ? dispatch(mixtureActions.fetchStatuses(data))
      : dispatch(mixtureActions.requestFailure(error!));
  };
};

export const fetchFilteredMixtures = (queryStr: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(MixtureTypes.FetchFiltered));
    const { ok, data, error } = await getRequest<DataResult>(
      `/mixtures/filtered?${queryStr}`
    );
    dispatch(requestEnd(MixtureTypes.FetchFiltered));

    return ok
      ? dispatch(mixtureActions.fetchFiltered(data))
      : dispatch(mixtureActions.requestFailure(error!));
  };
};

export const createMixture = (mixture: MixtureEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest('/mixtures', mixture);
    return ok
      ? dispatch(alertSuccess(i18n.t('mixture.created')))
      : dispatch(mixtureActions.requestFailure(error!));
  };
};

export const editMixture = (id: number, mixture: MixtureEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/mixtures/${id}`, mixture);
    return ok
      ? dispatch(alertSuccess(i18n.t('mixture.updated')))
      : dispatch(mixtureActions.requestFailure(error!));
  };
};

export const deleteMixture = (id: number, mixture: MixtureDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/mixtures/${id}`, mixture);
    if (ok) {
      dispatch(alertSuccess(i18n.t('mixture.deleted')));
      return dispatch(mixtureActions.delete(mixture));
    }
    dispatch(alertError(error?.errors?.detail ?? i18n.t('delete.failed')));
    return dispatch(mixtureActions.requestFailure(error!));
  };
};

export const fetchMixtureAvgParameters = (id: number) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await getRequest<MixtureAvgParameterDto[]>(
      `/mixtures/${id}/avgparameters`
    );
    return ok
      ? dispatch(mixtureActions.fetchMixtureAvgParameters(data))
      : dispatch(mixtureActions.requestFailure(error!));
  };
};

export const fetchMixtureStdPractice = (id: number) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await getRequest<MixtureStdPracticeDto[]>(
      `/mixtures/${id}/stdpractice`
    );
    return ok
      ? dispatch(mixtureActions.fetchMixtureStdPractice(data))
      : dispatch(mixtureActions.requestFailure(error!));
  };
};

export const fetchMixtureSpecifications = (id: number) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await getRequest<MixtureSpecificationDto[]>(
      `/mixtures/${id}/specification`
    );
    return ok
      ? dispatch(mixtureActions.fetchMixtureSpecifications(data))
      : dispatch(mixtureActions.requestFailure(error!));
  };
};

export const fetchNumberOfOngoingMixtures = () => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await getRequest<number>('/mixtures/ongoing');
    return ok
      ? dispatch(mixtureActions.fetchNumberOfOngoingMixtures(data))
      : dispatch(mixtureActions.requestFailure(error!));
  };
};
